<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Previous documents"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasPreviousDocumentsData"
            :has-errors="hasSectionErrors"
          />
          Previous documents
        </span>
      </template>
      <div
        v-for="(doc, docIndex) in parcel.previous_documents"
        :key="docIndex"
        class="border rounded pl-3 pr-3 pt-1 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Document type"
              rules=""
            >
              <b-form-group>
                <label>
                  Document type <small class="text-muted">1201002000</small>
                </label>
                <classifier-select
                  v-model="doc.document_type"
                  :classifier-key="classifierKeys.previousDocumentTypes"
                  :country="previousDocumentCountry"
                  :empty-code-selectable="previousDocumentCountry === 'NL'"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Reference number"
            >
              <b-form-group>
                <label>
                  Reference number
                  <small class="text-muted">1201001000</small>
                </label>
                <b-form-input
                  v-model="doc.reference_number"
                  v-b-tooltip.hover.top="getIssueMessage(`previous_documents.${docIndex}.reference_number`)"
                  :class="getIssueClass(`previous_documents.${docIndex}.reference_number`)"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Transport document reference number"
            >
              <b-form-group>
                <label>
                  Transport document reference number
                  <small class="text-muted">1205001000</small>
                </label>
                <b-form-input
                  v-model="doc.transport_document_reference"
                  v-b-tooltip.hover.top="getIssueMessage(`previous_documents.${docIndex}.transport_document_reference`)"
                  :class="getIssueClass(`previous_documents.${docIndex}.transport_document_reference`)"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="2"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Date"
            >
              <b-form-group>
                <label>
                  Date
                  <small class="text-muted">1509000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Date of the previous document'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="doc.date"
                  v-b-tooltip.hover.top="getIssueMessage(`previous_documents.${docIndex}.date`)"
                  :class="getIssueClass(`previous_documents.${docIndex}.date`)"
                  type="date"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="1"
            class="text-right"
          >
            <b-button
              v-if="!disabled"
              variant="link"
              size="sm"
              class="text-danger mt-2"
              @click="removeDocument(docIndex)"
            >
              <feather-icon icon="TrashIcon" />
              Delete
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-row>
        <b-col class="text-right">
          <b-button
            v-if="!disabled"
            variant="info"
            size="sm"
            @click="addDocument"
          >
            <feather-icon icon="PlusIcon" />
            New document
          </b-button>
        </b-col>
      </b-row>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'
import ClassifierSelect from '@/views/components/ClassifierSelect.vue'
import { CUSTOMS_CLASSIFIER_KEYS, classifierCountryParam } from '@/libs/customsClassifiers'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    ValidationProvider,
    ClassifierSelect,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    entryCountry: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      classifierKeys: CUSTOMS_CLASSIFIER_KEYS,
    }
  },
  computed: {
    // The list comes from the shared classifier dropdown, which resolves the
    // per-country variant (Tulli CL214 + overlays for FI, EMTA + master-level
    // pseudo-entries for EE), owns the request and the cache it shares with the
    // send modal, and reloads when the entry country changes.
    previousDocumentCountry() {
      return classifierCountryParam(this.entryCountry)
    },
    hasPreviousDocumentsData() {
      if (!this.parcel || !this.parcel.previous_documents || !Array.isArray(this.parcel.previous_documents)) {
        return false
      }
      return this.parcel.previous_documents.some(doc => doc.document_type || doc.reference_number || doc.transport_document_reference || doc.date)
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('previous_documents.'))
    },
  },
  methods: {
    addDocument() {
      if (!this.parcel.previous_documents) {
        this.$set(this.parcel, 'previous_documents', [])
      }
      this.parcel.previous_documents.push({
        document_type: '',
        reference_number: '',
        transport_document_reference: '',
        date: '',
      })
    },
    removeDocument(docIndex) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this document?', {
        title: 'Please Confirm',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        centered: true,
      }).then(value => {
        if (value) {
          this.parcel.previous_documents.splice(docIndex, 1)
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
